import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Handshake, Calendar, Users, Building2, CheckCircle, Clock, Info, FileText, BarChart3, ExternalLink, CheckSquare, FileImage, AlertCircle, XCircle, Download, MessageCircle, Instagram, Youtube, Link as LinkIcon, Loader, User } from 'lucide-react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Collab } from '../lib/firebase';

export default function PublicCampaignView() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Collab | null>(null);
  const [timelineItems, setTimelineItems] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [filteredTimelineItems, setFilteredTimelineItems] = useState<any[]>([]);
  const [filteredDocuments, setFilteredDocuments] = useState<any[]>([]); 
  const [filteredStats, setFilteredStats] = useState<any[]>([]); 
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDebugMode, setIsDebugMode] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [agencyName, setAgencyName] = useState('');
  const [currentInfluencerName, setCurrentInfluencerName] = useState<string | null>(null);
  const [currentInfluencer, setCurrentInfluencer] = useState<any | null>(null);
  const [currentInfluencerId, setCurrentInfluencerId] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [influencerProfiles, setInfluencerProfiles] = useState<any[]>([]);

  useEffect(() => {
    if (token) {
      loadCampaignData();
    }
  }, [token]);

  // Effet pour filtrer les données quand l'influenceur change
  useEffect(() => {
    if (timelineItems.length > 0 || documents.length > 0 || stats.length > 0) {
      console.log(`Filtrage des données pour l'influenceur ID: ${currentInfluencerId || 'tous'}`);
      
      // Fonction de filtrage améliorée
      const filterByInfluencer = (item: any) => {
        // Si pas d'influenceur spécifié, montrer tout
        if (!currentInfluencerId) return true;
        
        // Si l'item n'a pas d'influencerId ou est vide/null, c'est un élément global
        const itemInfluencerId = item.influencerId;
        
        if (itemInfluencerId === undefined || 
            itemInfluencerId === null || 
            itemInfluencerId === '') {
          return true; // Élément global visible par tous
        }
        
        // Sinon, vérifier si l'ID correspond exactement
        return itemInfluencerId === currentInfluencerId;
      };
      
      // Filtrer les éléments de timeline
      const filteredTimeline = timelineItems.filter(filterByInfluencer);
      setFilteredTimelineItems(filteredTimeline);
      
      // Filtrer les documents
      const filteredDocs = documents.filter(filterByInfluencer);
      setFilteredDocuments(filteredDocs);
      
      // Filtrer les statistiques
      const filteredStatistics = stats.filter(filterByInfluencer);
      setFilteredStats(filteredStatistics);
      
      console.log(`Filtrage terminé: ${filteredTimeline.length}/${timelineItems.length} timeline, ${filteredDocs.length}/${documents.length} documents, ${filteredStatistics.length}/${stats.length} stats`);
    } else {
      // Si pas de données, initialiser avec des tableaux vides
      setFilteredTimelineItems([]);
      setFilteredDocuments([]);
      setFilteredStats([]);
    }
  }, [currentInfluencerId, timelineItems, documents, stats]);

  const loadCampaignData = async () => {
    try {
      console.log('Chargement des données pour le token:', token);
      
      // Check if we're coming from an influencer view
      const urlParams = new URLSearchParams(window.location.search);
      const influencerName = urlParams.get('influencer');
      
      if (influencerName && influencerName.trim()) {
        const normalizedInfluencerName = influencerName.trim();
        console.log(`Influenceur spécifié dans l'URL: "${normalizedInfluencerName}"`);
        setCurrentInfluencerName(normalizedInfluencerName);
      }
      
      setIsLoading(true);
      setError(null); 

      // Vérifier si c'est un token temporaire
      const isTemporaryToken = token?.startsWith('temp-');
      
      let campaignData: Collab | null = null;
      
      if (isTemporaryToken) {
        // Pour un token temporaire, on récupère toutes les collaborations et on filtre
        const collabsSnapshot = await getDocs(collection(db, 'collabs'));
        
        // Extraire l'ID partiel du token temporaire
        const partialId = token.substring(5); // Enlever 'temp-'
        
        // Chercher une collaboration dont l'ID commence par le partialId
        const matchingCollab = collabsSnapshot.docs.find(doc => 
          doc.id.startsWith(partialId)
        );
        
        if (matchingCollab) {
          campaignData = { 
            id: matchingCollab.id, 
            ...matchingCollab.data(),
            // Forcer ces valeurs pour un accès temporaire
            linkActive: true,
            linkToken: token
          } as Collab;
        }
      } else {
        // Rechercher la collaboration par token normal
        const collabsQuery = query(
          collection(db, 'collabs'),
          where('linkToken', '==', token)
        );
        
        const collabsSnapshot = await getDocs(collabsQuery);
        
        if (!collabsSnapshot.empty) {
          campaignData = { 
            id: collabsSnapshot.docs[0].id, 
            ...collabsSnapshot.docs[0].data() 
          } as Collab;
        }
      }
      
      if (!campaignData) {
        console.error('Aucune collaboration trouvée avec ce token:', token);
        setError('Cette campagne n\'existe pas ou le lien n\'est plus actif.');
        setDebugInfo({
          token,
          error: 'Aucune collaboration trouvée',
          timestamp: new Date().toISOString()
        });
        setIsLoading(false);
        return;
      }
            
      console.log('Campagne trouvée:', campaignData);
      setCampaign(campaignData);
      
      // Trouver l'influenceur correspondant si un nom est fourni
      if (influencerName && campaignData.influencers && campaignData.influencers.length > 0) {
        const normalizedInfluencerName = influencerName.trim().toLowerCase();
        console.log(`Recherche de l'influenceur: "${normalizedInfluencerName}"`);
        console.log('Influenceurs disponibles:', campaignData.influencers.map(inf => ({
          name: inf.fullName,
          id: inf.id
        })));
        
        const matchingInfluencer = campaignData.influencers.find(inf => 
          (inf.fullName || '').toLowerCase().trim() === normalizedInfluencerName
        );
        
        if (matchingInfluencer) {
          console.log('Influenceur trouvé:', matchingInfluencer);
          setCurrentInfluencer(matchingInfluencer);
          setCurrentInfluencerId(matchingInfluencer.id || null);
          console.log('ID de l\'influenceur trouvé:', matchingInfluencer.id);
          
          // Si l'ID est une chaîne vide, considérer comme null
          if (matchingInfluencer.id === '') {
            console.warn('L\'ID de l\'influenceur est une chaîne vide, considéré comme null');
            setCurrentInfluencerId(null);
          }
        } else {
          console.warn(`Aucun influenceur trouvé avec le nom: ${influencerName}`);
        }
      }
      
      // Récupérer le nom de l'agence
      if (campaignData.agencyId) {
        try {
          const agencyDoc = await getDoc(doc(db, 'agencies', campaignData.agencyId));
          if (agencyDoc.exists()) {
            setAgencyName(agencyDoc.data().name || campaignData.agencyName || '');
          } else if (campaignData.agencyName) {
            setAgencyName(campaignData.agencyName);
          }
        } catch (agencyError) {
          console.error('Erreur lors de la récupération de l\'agence:', agencyError);
          // Utiliser le nom de l'agence stocké dans la campagne si disponible
          if (campaignData.agencyName) {
            setAgencyName(campaignData.agencyName);
          }
        }
      }
      
      // Récupérer les éléments de timeline
      try {
        if (campaignData.id) {
          const timelineQuery = query(
            collection(db, 'timelineItems'),
            where('collabId', '==', campaignData.id)
          );
        
          const timelineSnapshot = await getDocs(timelineQuery);
          const timelineData = timelineSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .sort((a, b) => a.order - b.order);
          
          // Normaliser les données de timeline
          for (const item of timelineData) {
            // Normaliser: considérer les chaînes vides comme null
            if (item.influencerId === '') {
              item.influencerId = null;
            }
            
            // Ajouter le nom de l'influenceur si possible
            if (item.influencerId) {
              const influencer = campaignData.influencers.find(inf => inf.id === item.influencerId);
              if (influencer) {
                item.influencerName = influencer.fullName;
              }
            }
          }
          
          setTimelineItems(timelineData); 
          setFilteredTimelineItems(timelineData);
          
          console.log('Éléments de timeline chargés:', timelineData.length, 
                     'avec influencerId:', timelineData.filter(item => item.influencerId).length);
        }
      } catch (timelineError) {
        console.error('Erreur lors de la récupération des éléments de timeline:', timelineError);
        // Ne pas bloquer le chargement si les éléments de timeline ne sont pas trouvés
      }
      
      // Récupérer les documents (uniquement ceux marqués comme visibles pour la marque)
      try {
        if (campaignData.id) {
          const documentsQuery = query(
            collection(db, 'collabDocuments'),
            where('collabId', '==', campaignData.id),
            where('visibleForBrand', '==', true)
          );
        
          const documentsSnapshot = await getDocs(documentsQuery);
          const documentsData = documentsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Normaliser les données de documents
          for (const doc of documentsData) {
            // Normaliser: considérer les chaînes vides comme null
            if (doc.influencerId === '') {
              doc.influencerId = null;
            }
            
            // Ajouter le nom de l'influenceur si possible
            if (doc.influencerId) {
              const influencer = campaignData.influencers.find(inf => inf.id === doc.influencerId);
              if (influencer) {
                doc.influencerName = influencer.fullName;
              }
            }
          }
        
          setDocuments(documentsData);
          setFilteredDocuments(documentsData);
          console.log('Documents chargés:', documentsData.length);
        }
      } catch (documentsError) {
        console.error('Erreur lors de la récupération des documents:', documentsError);
        // Ne pas bloquer le chargement si les documents ne sont pas trouvés
      }
      
      // Récupérer les statistiques
      try {
        if (campaignData.id) {
          const statsQuery = query(
            collection(db, 'collabStats'),
            where('collabId', '==', campaignData.id)
          );
        
          const statsSnapshot = await getDocs(statsQuery);
          const statsData = statsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Normaliser les données de statistiques
          for (const stat of statsData) {
            // Normaliser: considérer les chaînes vides comme null
            if (stat.influencerId === '') {
              stat.influencerId = null;
            }
            
            // Ajouter le nom de l'influenceur si possible
            if (stat.influencerId) {
              const influencer = campaignData.influencers.find(inf => inf.id === stat.influencerId);
              if (influencer) {
                stat.influencerName = influencer.fullName;
              }
            }
          }
        
          setStats(statsData);
          setFilteredStats(statsData);
          console.log('Statistiques chargées:', statsData.length);
        }
      } catch (statsError) {
        console.error('Erreur lors de la récupération des statistiques:', statsError);
        // Ne pas bloquer le chargement si les statistiques ne sont pas trouvées
      }
      
      // Ajouter un bouton de debug caché (triple-clic sur le footer)
      setTimeout(() => {
        const footer = document.querySelector('footer');
        if (footer) footer.addEventListener('click', (e) => e.detail === 3 && setIsDebugMode(prev => !prev));
      }, 1000);
      
      setDebugInfo({
        token,
        campaignId: campaignData.id,
        campaignName: campaignData.name,
        linkActive: campaignData.linkActive,
        timelineCount: timelineItems.length,
        documentsCount: documents.length,
        currentInfluencerId,
        timestamp: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('Error loading campaign data:', error);
      setError('Une erreur est survenue lors du chargement des données.');
      setDebugInfo({
        token,
        error: error instanceof Error ? error.message : 'Erreur inconnue',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction utilitaire pour convertir une valeur en nombre
  const toNumber = (value: string | number | undefined): number => {
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    if (typeof value === 'number') return value;
    return 0;
  };

  // Fonction pour obtenir l'icône d'une plateforme sociale
  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'Instagram':
        return <Instagram className="w-4 h-4" />;
      case 'YouTube':
        return <Youtube className="w-4 h-4" />;
      case 'TikTok':
      case 'Twitch':
      default:
        return <MessageCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return 'Date non définie';
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'late':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <CheckSquare className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'done':
        return 'Terminé';
      case 'in_progress':
        return 'En cours';
      case 'late':
        return 'En retard';
      default:
        return 'À faire';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'late':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.invoice_sent || campaign.payment_received) return { label: 'Terminée', className: 'bg-green-500/20 text-green-300 border-green-500/20' };
    if (campaign.quote_sent || campaign.contract_signed) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20' };
    return { label: 'En attente', className: 'bg-blue-500/20 text-blue-300 border-blue-500/20' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-lg w-full bg-white rounded-xl shadow-lg p-8 text-center animate-fadeIn">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Lien de campagne non valide</h1>
          <p className="text-gray-600 mb-6">
            {error || "Ce lien de suivi n'est plus actif ou n'existe pas. Veuillez contacter l'agence pour obtenir un nouveau lien."}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
            <div className="flex items-start">
              <Info className="w-5 h-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <p className="text-sm text-blue-800">
                  <strong>Information technique:</strong> Si vous êtes l'administrateur de cette campagne, vérifiez que:
                </p>
                <ul className="text-sm text-blue-700 list-disc pl-5 mt-2">
                  <li>Le lien de partage a été correctement généré</li>
                  <li>L'option "Lien public" est activée pour cette campagne</li>
                  <li>Les règles de sécurité Firestore permettent l'accès public</li>
                </ul>
                <div className="mt-2 p-2 bg-blue-100 rounded text-xs font-mono overflow-auto">
                  <pre>{JSON.stringify(debugInfo, null, 2)}</pre>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Retour à l'accueil
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-fadeIn">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
                <Handshake className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{campaign.name}</h1>
                <p className="text-sm text-gray-500">Suivi de campagne • {agencyName || 'Le Suivi'}</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Dernière mise à jour: {formatDate(campaign.updatedAt)}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-500">Marque</h2>
              <Building2 className="w-5 h-5 text-indigo-600" />
            </div>
            <p className="text-xl font-semibold text-gray-900">{campaign.brand}</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-500">Date</h2>
              <Calendar className="w-5 h-5 text-indigo-600" />
            </div>
            <p className="text-xl font-semibold text-gray-900">{formatDate(campaign.date)}</p>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-medium text-gray-500">Chef de projet</h2>
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
            <p className="text-xl font-semibold text-gray-900">{campaign.manager}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {[
                { id: 'timeline', label: 'Timeline', icon: Calendar },
                { id: 'influencers', label: 'Influenceurs', icon: Users },
                { id: 'documents', label: 'Documents et preview', icon: FileImage }, 
                { id: 'stats', label: 'Statistiques', icon: BarChart3 } 
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center px-6 py-4 font-medium text-sm border-b-2 ${
                      activeTab === tab.id
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Timeline Tab */}
            {activeTab === 'timeline' && (
              <div className="space-y-6">
                <h2 className="text-lg font-medium text-gray-900 mb-6">
                  {currentInfluencerName ? 'Vos étapes' : 'Avancement de la campagne'}
                </h2>

                {isDebugMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs font-mono">
                    <p><strong>Mode debug</strong></p>
                    <p>Influenceur: {currentInfluencerName || 'Aucun'} (ID: {currentInfluencerId || 'null'})</p>
                    <p>Timeline filtrée: {filteredTimelineItems.length} / {timelineItems.length}</p>
                    <details>
                      <summary className="cursor-pointer text-blue-600">Afficher les IDs des items</summary>
                      <div className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                        {timelineItems.map((item, i) => (
                          <div key={i} className={
                            !currentInfluencerId || 
                            !item.influencerId || 
                            item.influencerId === currentInfluencerId 
                              ? "text-green-600" 
                              : "text-red-600"
                          }>
                            {i+1}. {item.stepName}: influencerId={item.influencerId || 'null'} 
                            {(!currentInfluencerId || !item.influencerId || item.influencerId === currentInfluencerId) ? "✓" : "✗"}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
                
                {filteredTimelineItems.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {currentInfluencerName 
                        ? "Aucune étape n'a encore été définie pour vous dans cette campagne" 
                        : "Aucune étape n'a encore été définie pour cette campagne"}
                    </p>
                    <p className="text-gray-400 text-sm mt-4">Les étapes de la campagne apparaîtront ici une fois qu'elles seront définies.</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {filteredTimelineItems.map((item, index) => (
                      <div key={item.id} className="relative">
                        {index < filteredTimelineItems.length - 1 && (
                          <div className="absolute top-8 left-4 bottom-0 w-0.5 bg-gray-200"></div>
                        )}
                        
                        <div className="flex items-start gap-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getStatusClass(item.status)}`}>
                            {getStatusIcon(item.status)}
                          </div>
                          
                          <div className="flex-1 bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="font-medium text-gray-900">{item.stepName}</h3>
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusClass(item.status)}`}>
                                {getStatusText(item.status)}
                              </span>
                            </div>
                            
                            {item.deadline && (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Calendar className="w-4 h-4" />
                                <span>Échéance: {formatDate(item.deadline)}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}


            {/* Influencers Tab */}
            {activeTab === 'influencers' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">Influenceurs sélectionnés</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {campaign.influencers && campaign.influencers.length > 0 ? (
                    // Filter influencers if viewing as a specific influencer
                    campaign.influencers
                    .filter(influencer => !currentInfluencerName || influencer.fullName === currentInfluencerName)
                    .map((influencer, index) => (
                      <div key={index} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900">{influencer.fullName}</h3>
                          </div>
                        </div>
                        
                        {influencer.deliverables && influencer.deliverables.length > 0 && (
                          <div className="mt-2">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Livrables:</h4>
                            <div className="space-y-3">
                              {influencer.deliverables.map((deliverable: any, idx: number) => (
                                <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                                  <span className="text-gray-800 font-medium">{deliverable.type}</span>
                                  <span className="font-semibold text-indigo-600">{formatCurrency(deliverable.price || '0')}</span>
                                </div>
                              ))}
                            </div>
                            <div className="mt-4 text-right">
                              <span className="text-sm text-gray-500">Total:</span>
                              <span className="ml-2 text-lg font-semibold text-indigo-600">
                                {formatCurrency(influencer.rate || '0')}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="col-span-2 text-center py-12 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                      <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">Aucun influenceur associé à cette campagne</p>
                    </div>
                  )}
                  
                  {currentInfluencerName && campaign.influencers && campaign.influencers.length > 1 && (
                    <div className="col-span-2 mt-4 bg-blue-50 border border-blue-100 rounded-lg p-4 text-blue-800">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p>
                          Vous visualisez uniquement vos informations pour cette campagne. 
                          {campaign.influencers.length - 1} autre(s) influenceur(s) participe(nt) également à cette collaboration.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Documents Tab */}
            {activeTab === 'documents' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">
                  {currentInfluencerName ? 'Vos documents' : 'Documents et preview'}
                </h2>
                
               {isDebugMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs font-mono">
                    <p><strong>Mode debug</strong></p>
                    <p>Documents filtrés: {filteredDocuments.length} / {documents.length}</p>
                    <details>
                      <summary className="cursor-pointer text-blue-600">Afficher les IDs des documents</summary>
                      <div className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                        {documents.map((doc, i) => (
                          <div key={i} className={
                            !currentInfluencerId || 
                            !doc.influencerId || 
                            doc.influencerId === currentInfluencerId 
                              ? "text-green-600" 
                              : "text-red-600"
                          }>
                            {i+1}. {doc.name}: influencerId={doc.influencerId || 'null'} 
                            {(!currentInfluencerId || !doc.influencerId || doc.influencerId === currentInfluencerId) ? "✓" : "✗"}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
                
                {filteredDocuments.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                    <FileImage className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {currentInfluencerName 
                        ? "Aucun document n'est disponible pour vous dans cette campagne" 
                        : "Aucun document ou aperçu disponible pour cette campagne"}
                    </p>
                    <p className="text-gray-400 text-sm mt-4">Les documents partagés par l'agence apparaîtront ici.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredDocuments.map((document) => (
                      <div key={document.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate">{document.name}</h3>
                            <p className="text-sm text-gray-500">{document.type}</p>
                            <p className="text-sm text-gray-500 mt-1">Ajouté le {formatDate(document.createdAt)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">{formatFileSize(document.fileSize)}</span>
                          <a
                            href={document.fileUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800"
                          >
                            <Download className="w-4 h-4" />
                            <span>Télécharger</span>
                          </a>
                        </div>
                        
                        {document.type === 'preview' && document.fileUrl && (
                          <div className="mt-4">
                            <div className="aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                              <img 
                                src={document.fileUrl}
                                alt="Aperçu" 
                                className="w-full h-full object-cover"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stats Tab */}
            {activeTab === 'stats' && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-6">
                  {currentInfluencerName ? 'Vos statistiques' : 'Statistiques de performance'}
                </h2>
                
               {isDebugMode && (
                  <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs font-mono">
                    <p><strong>Mode debug</strong></p>
                    <p>Stats filtrées: {filteredStats.length} / {stats.length}</p>
                    <details>
                      <summary className="cursor-pointer text-blue-600">Afficher les IDs des stats</summary>
                      <div className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-32">
                        {stats.map((stat, i) => (
                          <div key={i} className={
                            !currentInfluencerId || 
                            !stat.influencerId || 
                            stat.influencerId === currentInfluencerId 
                              ? "text-green-600" 
                              : "text-red-600"
                          }>
                            {i+1}. {stat.creator}: influencerId={stat.influencerId || 'null'} 
                            {(!currentInfluencerId || !stat.influencerId || stat.influencerId === currentInfluencerId) ? "✓" : "✗"}
                          </div>
                        ))}
                      </div>
                    </details>
                  </div>
                )}
                
                {filteredStats.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                    <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {currentInfluencerName 
                        ? "Aucune statistique n'est disponible pour vous dans cette campagne" 
                        : "Aucune statistique de performance disponible"}
                    </p>
                    <p className="text-gray-400 text-sm mt-4">Les statistiques des publications seront affichées ici une fois disponibles.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredStats.map((stat) => (
                      <div key={stat.id} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                        {stat.postThumb && (
                          <div className="mb-4 aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                            <img
                              src={stat.postThumb}
                              alt="Aperçu du post"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        <div className="space-y-3">
                          <h3 className="font-medium text-gray-900">{stat.creator}</h3>
                          
                          <div className="grid grid-cols-3 gap-3">
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-gray-500 mb-1">Vues</p>
                              <p className="text-lg font-semibold text-gray-900">{typeof stat.views === 'number' ? stat.views.toLocaleString() : '0'}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-gray-500 mb-1">Likes</p>
                              <p className="text-lg font-semibold text-gray-900">{typeof stat.likes === 'number' ? stat.likes.toLocaleString() : '0'}</p>
                            </div>
                            <div className="bg-gray-50 rounded-lg p-3 text-center">
                              <p className="text-xs text-gray-500 mb-1">Engagement</p>
                              <p className="text-lg font-semibold text-gray-900">{stat.engagementRate || '0'}%</p>
                            </div>
                          </div>
                          
                          {stat.postUrl && (
                            <a
                              href={stat.postUrl || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors text-sm font-medium"
                            >
                              <ExternalLink className="w-4 h-4" />
                              <span>Voir le post</span>
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Campaign Summary */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-6">Récapitulatif de la campagne</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Détails de la campagne</h3>
                <div className="space-y-2">                      
                  <div className="flex justify-between">
                    <span className="text-gray-600">{currentInfluencerName ? 'Votre budget:' : 'Budget total:'}</span>
                    <span className="font-medium text-gray-900">{
                      currentInfluencer 
                      ? formatCurrency(toNumber(currentInfluencer.rate))
                      : formatCurrency(
                          campaign.budget || 
                          campaign.influencers?.reduce((sum, inf) => sum + toNumber(inf.rate), 0) || 
                          0
                        )
                    }</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Statut:</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getCampaignStatus(campaign).className.replace('border-', '')}`}>
                      {getCampaignStatus(campaign).label}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Étapes clés</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Devis envoyé</span>
                    {campaign.quote_sent ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Contrat signé</span>
                    {campaign.contract_signed ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Facture envoyée</span>
                    {campaign.invoice_sent ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Paiement reçu</span>
                    {campaign.payment_received ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <Clock className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Résumé des influenceurs</h3>
                {currentInfluencerName ? (
                  // Show only the current influencer if we're in influencer view
                  <div className="space-y-3">
                    {campaign.influencers && campaign.influencers
                      .filter(influencer => influencer.fullName === currentInfluencerName)
                      .map((influencer, index) => (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-indigo-600" />
                            </div>
                            <span className="text-gray-900 truncate">{influencer.fullName}</span>
                          </div>
                          <span className="font-medium text-indigo-600 flex-shrink-0">{formatCurrency(toNumber(influencer.rate))}</span>
                        </div>
                      ))}
                    {campaign.influencers && campaign.influencers.some(inf => inf.fullName === currentInfluencerName) && (
                      <div className="text-sm text-blue-600 italic mt-2">
                        Vous visualisez uniquement vos informations pour cette campagne
                      </div>
                    )}
                  </div>
                ) : (
                  // Show all influencers in normal view
                  <div className="space-y-3">
                    {campaign.influencers && campaign.influencers.map((influencer, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <Users className="w-4 h-4 text-indigo-600" />
                          </div>
                          <span className="text-gray-900">{influencer.fullName}</span>
                        </div>
                        <span className="font-medium text-indigo-600">{formatCurrency(influencer.rate || '0')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Informations de contact</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span className="text-gray-900">Chef de projet: {campaign.manager}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {agencyName && (
                      <>
                        <Building2 className="w-4 h-4 text-indigo-600" />
                        <span className="text-gray-900">Agence: {agencyName}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center mb-4 md:mb-0" title={isDebugMode ? "Mode debug activé" : "Triple-cliquez pour le mode debug"}>
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center flex-shrink-0">
                <Handshake className="w-5 h-5 text-white" />
              </div>
              <span className="ml-2 text-lg font-semibold text-gray-900">{agencyName}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LinkIcon className="w-4 h-4" />
              <span>Ce lien de suivi est privé et confidentiel. Merci de ne pas le partager.</span>
              {isDebugMode && (
                <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono">
                  <p>Debug: {JSON.stringify({
                    influencerName: currentInfluencerName,
                    influencerId: currentInfluencerId,
                    timelineCount: filteredTimelineItems.length,
                    documentsCount: filteredDocuments.length,
                    statsCount: filteredStats.length
                  }, null, 2)}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}